import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { MdPeople, MdPerson, MdPublic, MdAttachMoney, MdAccountBalanceWallet, MdAccountCircle } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatsGrid from '../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../shared/layout/cards/StatCard';
import Row from '../../../shared/layout/containers/Row';
import Column from '../../../shared/layout/containers/Column';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import CardHeader from '../../../shared/layout/cards/CardHeader';
import CardTitle from '../../../shared/layout/cards/CardTitle';
import CardBody from '../../../shared/layout/cards/CardBody';
import ListGroup from '../../../shared/layout/containers/ListGroup';
import ListItem from '../../../shared/layout/containers/ListItem';
import ProfileContainer from '../../../shared/layout/containers/ProfileContainer';
import ProfileName from '../../../shared/layout/typography/ProfileName';
import ItemCount from '../../../shared/layout/typography/ItemCount';
import Badge from '../../../shared/layout/typography/Badge';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import SalesByCountries from '../Users/components/SalesByCountries';
import { fetchAdminUserStatistics } from '../../../services/Http/AdminApi';
import { useCurrency } from '../../../context/CurrencyProvider';

const AdminUserStatistics = ({ embedded = false }) => {
    const { formatEurAmount } = useCurrency();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const getStatistics = async () => {
            try {
                const data = await fetchAdminUserStatistics();
                setStatistics(data && Object.keys(data).length > 0 ? data : null);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        getStatistics();
    }, []);

    if (loading) return embedded ? <LoadingMessage>Loading user statistics...</LoadingMessage> : <PageContainer><LoadingMessage>Loading user statistics...</LoadingMessage></PageContainer>;
    if (error) return embedded ? <ErrorMessage>Failed to load user statistics.</ErrorMessage> : <PageContainer><ErrorMessage>Failed to load user statistics.</ErrorMessage></PageContainer>;
    if (!statistics) return embedded ? <ErrorMessage>No statistics available.</ErrorMessage> : <PageContainer><ErrorMessage>No statistics available.</ErrorMessage></PageContainer>;

    const content = (
        <>
            <StatsGrid>
                <StatCard
                    icon={<MdPeople size={30} className="icon" />}
                    title="Total buyers"
                    value={statistics.total_buyers?.toLocaleString() ?? 0}
                    color="primary"
                />
                <StatCard
                    icon={<MdPerson size={30} className="icon" />}
                    title="Total buyers (own)"
                    value={statistics.total_buyers_own?.toLocaleString() ?? 0}
                    color="success"
                />
                <StatCard
                    icon={<MdPublic size={30} className="icon" />}
                    title="Total buyers (global)"
                    value={statistics.total_buyers_global?.toLocaleString() ?? 0}
                    color="info"
                />
                <StatCard
                    icon={<MdAttachMoney size={30} className="icon" />}
                    title="Total revenue (own)"
                    value={formatEurAmount(statistics.total_revenue_own)}
                    color="warning"
                />
                <StatCard
                    icon={<MdAccountBalanceWallet size={30} className="icon" />}
                    title="Total revenue (global)"
                    value={formatEurAmount(statistics.total_revenue_global)}
                    color="danger"
                />
            </StatsGrid>
            <Row>
                <Column>
                    <SalesByCountries data={statistics.summary_by_countries || []} />
                </Column>
                <Column>
                    <StyledCard>
                        <CardHeader>
                            <CardTitle>Top buyers by items</CardTitle>
                        </CardHeader>
                        <CardBody textSize="extraSmall">
                            <ListGroup>
                                {(statistics.top_buyers_by_items || []).map((buyer, index) => (
                                    <ListItem key={`${buyer.user_id}-${index}`}>
                                        <ProfileContainer>
                                            <AvatarIcon size={24} />
                                            <ProfileName>{buyer.user_name} {buyer.user_surname}</ProfileName>
                                        </ProfileContainer>
                                        <ItemCount>{buyer.count_of_items} items</ItemCount>
                                        <Badge ownBuyer={buyer.own_buyer}>
                                            {buyer.own_buyer ? 'Own' : 'Global'}
                                        </Badge>
                                    </ListItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </StyledCard>
                </Column>
                <Column half>
                    <StyledCard>
                        <CardHeader>
                            <CardTitle>Top buyers by total spent</CardTitle>
                        </CardHeader>
                        <CardBody textSize="extraSmall">
                            <ListGroup>
                                {(statistics.top_buyers_by_total_spent || []).map((buyer, index) => (
                                    <ListItem key={`${buyer.user_id}-${index}`}>
                                        <ProfileContainer>
                                            <AvatarIcon size={40} />
                                            <ProfileName>{buyer.user_name} {buyer.user_surname}</ProfileName>
                                        </ProfileContainer>
                                        <ItemCount>{formatEurAmount(buyer.total_spent)}</ItemCount>
                                        <Badge ownBuyer={buyer.own_buyer}>
                                            {buyer.own_buyer ? 'Own' : 'Global'}
                                        </Badge>
                                    </ListItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </StyledCard>
                </Column>
            </Row>
        </>
    );

    if (embedded) {
        return content;
    }

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>System User Statistics</PageTitle>
            </PageHeader>
            <PageContent>{content}</PageContent>
        </PageContainer>
    );
};

export default AdminUserStatistics;

const AvatarIcon = styled(MdAccountCircle)`
    color: var(--color-primary);
    opacity: 0.85;
`;
